/**
 * 全屏详情页返回时的 PV 上报工具
 * 用于处理详情页返回详情页场景的 PV 埋点上报
 */
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import { useCallback } from "react";

/**
 * 获取 pin 的 raw_text（从不同数据结构中提取）
 */
function getPinRawText(pin: any): string | undefined {
  if (!pin) return undefined;
  return pin.raw_text || pin.pin?.raw_text;
}

/**
 * 上报详情页 PV
 * @param pin - 要上报的 pin 数据
 */
function reportPinDetailPv(pin: any): void {
  if (!pin) return;

  // 延迟上报，确保页面已经切换到目标详情页
  setTimeout(() => {
    pvTracker({
      ...getPageData(),
      scene_name: getPinRawText(pin),
    });
  }, 0);
}

/**
 * 判断是否需要上报 PV（详情页返回详情页的场景）
 * @param currentPinId - 当前显示的 pinId
 * @param targetPinId - 目标 pinId
 * @returns 是否需要上报 PV
 */
function shouldReportPv(
  currentPinId: string | number | null | undefined,
  targetPinId: string | number | null | undefined
): boolean {
  if (!currentPinId || !targetPinId) return false;
  return String(currentPinId) !== String(targetPinId);
}

/**
 * 处理详情页切换时的 PV 上报
 * @param options - 配置选项
 * @param options.currentPinId - 当前显示的 pinId
 * @param options.targetPinId - 目标 pinId
 * @param options.targetPin - 目标 pin 数据
 */
export function handlePinDetailPvReport({
  currentPinId,
  targetPinId,
  targetPin,
}: {
  currentPinId: string | number | null | undefined;
  targetPinId: string | number | null | undefined;
  targetPin: any;
}): void {
  // 只在详情页返回详情页时上报（切换到了不同的详情页）
  if (shouldReportPv(currentPinId, targetPinId)) {
    reportPinDetailPv(targetPin);
  }
}

/**
 * Hook: 用于全屏详情页返回时的 PV 上报
 * 返回一个函数用于处理返回详情页的 PV 上报
 */
export function usePinDetailPvReport() {
  /**
   * 处理返回详情页的 PV 上报
   * @param currentPinId - 当前显示的 pinId
   * @param targetPin - 返回后要显示的 pin 数据
   */
  const reportReturnPv = useCallback(
    (currentPinId: string | number | null | undefined, targetPin: any) => {
      if (!targetPin?.pinId) return;

      handlePinDetailPvReport({
        currentPinId,
        targetPinId: targetPin.pinId,
        targetPin,
      });
    },
    []
  );

  return { reportReturnPv };
}
